/**
 * API Documentation page
 */

import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {ROUTES} from '../../config/constants';

const endpoints = [
  {
    method: 'GET',
    path: '/api/v1/meetings',
    description: 'List all meetings',
    auth: 'Required',
  },
  {
    method: 'GET',
    path: '/api/v1/meetings/:id',
    description: 'Get meeting details',
    auth: 'Required',
  },
  {
    method: 'POST',
    path: '/api/v1/meetings',
    description: 'Create a new meeting',
    auth: 'Required',
  },
  {
    method: 'GET',
    path: '/api/v1/transcriptions/:id',
    description: 'Get transcription for a meeting',
    auth: 'Required',
  },
  {
    method: 'GET',
    path: '/api/v1/analytics',
    description: 'Get analytics data',
    auth: 'Required',
  },
];

const codeExamples = [
  {
    language: 'JavaScript',
    title: 'Create a Meeting',
    code: `const response = await fetch('https://api.voxapilot.com/v1/meetings', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    title: 'Team Standup',
    description: 'Daily team sync',
  }),
});

const meeting = await response.json();`,
  },
  {
    language: 'Python',
    title: 'List Meetings',
    code: `import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
}

response = requests.get(
    'https://api.voxapilot.com/v1/meetings',
    headers=headers
)

meetings = response.json()`,
  },
  {
    language: 'cURL',
    title: 'Get Transcription',
    code: `curl -X GET \\
  https://api.voxapilot.com/v1/transcriptions/meeting-id \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
  },
];

export function APIPage() {
  return (
    <LandingLayout>
      <Section padding="lg" background="white" className="pt-32 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <SectionHeader
            title="API Documentation"
            subtitle="Integrate VoxaPilot into your applications with our REST API"
            centered
            size="lg"
          />

          <div className="max-w-3xl mx-auto mb-16">
            <div className="prose prose-lg max-w-none text-textSecondary dark:text-[#858585] leading-relaxed space-y-4 text-center">
              <p>
                The VoxaPilot API allows you to programmatically access meeting data, transcriptions,
                and analytics. Build custom integrations, automate workflows, and extend VoxaPilot's
                capabilities.
              </p>
            </div>
          </div>

          {/* Quick Start */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-6">Quick Start</h2>
            <Card className="p-6 bg-gray-50 dark:bg-[#252526]">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">1. Get Your API Key</h3>
                  <p className="text-sm text-textSecondary dark:text-[#858585]">
                    Navigate to Settings → API Keys to generate your API key.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">2. Make Your First Request</h3>
                  <p className="text-sm text-textSecondary dark:text-[#858585] mb-2">
                    Include your API key in the Authorization header:
                  </p>
                  <pre className="bg-gray-900 dark:bg-[#1E1E1E] text-gray-100 dark:text-[#CCCCCC] p-4 rounded-lg text-sm overflow-x-auto border border-gray-700">
                    <code>{`Authorization: Bearer YOUR_API_KEY`}</code>
                  </pre>
                </div>
                <div>
                  <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">3. Base URL</h3>
                  <p className="text-sm text-textSecondary dark:text-[#858585]">
                    All API requests should be made to:{' '}
                    <code className="bg-gray-200 dark:bg-[#2D2D30] text-textPrimary dark:text-[#CCCCCC] px-2 py-1 rounded">https://api.voxapilot.com/v1</code>
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Endpoints */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-6">API Endpoints</h2>
            <div className="space-y-4">
              {endpoints.map((endpoint) => (
                <Card key={endpoint.path} className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <span
                        className={`
                          px-3 py-1 rounded text-sm font-medium
                          ${
                            endpoint.method === 'GET'
                              ? 'bg-blue-100 text-blue-800'
                              : endpoint.method === 'POST'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }
                        `}
                      >
                        {endpoint.method}
                      </span>
                      <code className="text-sm font-mono text-textPrimary dark:text-[#CCCCCC]">{endpoint.path}</code>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-textSecondary dark:text-[#858585]">{endpoint.description}</p>
                    </div>
                    <div>
                      <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-[#2D2D30] text-gray-600 dark:text-[#858585] rounded">
                        {endpoint.auth}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Code Examples */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-6">Code Examples</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {codeExamples.map((example) => (
                <Card key={example.title} className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-1">{example.title}</h3>
                      <p className="text-xs text-textSecondary dark:text-[#858585]">{example.language}</p>
                    </div>
                    <pre className="bg-gray-900 dark:bg-[#1E1E1E] text-gray-100 dark:text-[#CCCCCC] p-4 rounded-lg text-xs overflow-x-auto border border-gray-700">
                      <code>{example.code}</code>
                    </pre>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gray-50 dark:bg-[#252526] rounded-2xl p-12">
            <h2 className="text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-textSecondary dark:text-[#858585] mb-8 max-w-2xl mx-auto">
              Generate your API key and start building with VoxaPilot today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={ROUTES.SIGN_UP}>
                <Button variant="primary" size="lg">
                  Get API Key
                </Button>
              </a>
              <a href={ROUTES.CONTACT}>
                <Button variant="outline" size="lg">
                  Contact Sales
                </Button>
              </a>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

